import { GraphModel } from '../graph/graph.model';
import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import { ToolModel } from './tool.model';
import { Routes } from '../routes';
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';

type Params = {};

export class SaveToolAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Tool';
  description = 'Save current tool to the graph';
  inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = 'Tool';
  icon = 'save';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    },
    private readonly queryClient: QueryClient,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) && context.hasTargetedModelOfType(ToolModel)
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const tool = context.targetedModelOfType(ToolModel);

    Assert.assert(graph !== null, 'Graph not found');
    Assert.assert(tool !== null, 'Tool not found');

    const saveToast = toast.loading('Saving tool...', {
      id: 'save-tool',
    });

    const result = tool.compile({ setOperations: true });

    if (result.isErr()) {
      saveToast.error(JSON.stringify(result.error));
      return err([
        { t: result.error.t, message: JSON.stringify(result.error.e) },
      ]);
    }

    if (tool.newOperations.snapshot().length === 0) {
      saveToast.done();
      return ok({});
    }

    const stagingResult = await graph.attemptToStageOperations(
      tool.newOperations.snapshot()
    );

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };

      saveToast.error(error.message);
      return err([error]);
    }

    tool.markAsStaged();

    // Invalidate queries to refetch tools and connections
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.tools.all({ orgId: this.orgId }),
    });
    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.connections.all({ orgId: this.orgId }),
    });
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.tools.all({ orgId: this.orgId }),
    });
    await this.queryClient.refetchQueries({
      queryKey: queryKeys.connections.all({ orgId: this.orgId }),
    });

    saveToast.success(`Tool ${tool.name.snapshot()} saved`);

    if (tool.opts.isNew) {
      this.navigator.pushAndReplace({
        route: Routes.tool.forId(tool.name.snapshot()).toString(),
      });
    }

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    return context.targetedModelOfType(ToolModel)?.isNew ?? false;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(ToolModel);

    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                model={model}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  model: ToolModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState(model.name.snapshot());
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView title={`Save tool as`} className="px-4 py-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="tool-name" className="text-sm">
            Tool Name
          </Label>
          <Input
            id="tool-name"
            placeholder="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            disabled={loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              model.formState.set({
                ...model.formState.snapshot(),
                name,
              });
              await onRespond({});
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
